import { v4 as uuidv4 } from 'uuid';
import currency from 'helpers/currency';
import { SaleTypes } from 'modules/_common/SaleTypes';
import { committedQuantity, quantityFloorError } from './batchStock';
import { buyerMessagePayload, isBuyerMessageValid } from './buyerMessage';
import {
    EMPTY_PURCHASE_RULES,
    MAX_PER_ORDER_LIMIT,
    hasSaleStart,
    isMaxPerOrderValid,
    purchaseRulesFromTicket,
    purchaseRulesPayload,
} from './purchaseRules';

/**
 * Estado do formulário de ingressos e as contas que dependem só dele.
 *
 * O Lote 1 mora nos campos do próprio ingresso (`price`, `quantity`,
 * `batchName`, `batch_id`) e os seguintes em `batches` — é o formato que o
 * payload da API espera e que este arquivo preserva. A tela, porém, mostra
 * todos os lotes iguais: `lotsOf` e `patchLot` fazem a tradução, para nenhum
 * componente precisar saber que o primeiro lote é diferente.
 */

export const NAME_MAX = 30;
export const DESCRIPTION_MAX = 80;
export const HIDDEN_CODE_MAX = 30;
export const BATCH_NAME_MAX = 30;
export const MIN_PRICE = 5;

export function newTicket() {
    return {
        internal_id: uuidv4(),
        name: '',
        // Vazio = nome automático ("Inteira Lote 1"), ver `lotName`.
        batchName: '',
        quantity: '',
        description: '',
        price: '',
        floatPrice: 0,
        is_free: false,
        is_active: true,
        sale_type: SaleTypes.BATCH,
        absorbed_service_fee: false,
        requires_document: false,
        hidden_code: null,
        buyer_message: '',
        buyer_message_link_url: '',
        buyer_message_link_label: '',
        ...EMPTY_PURCHASE_RULES,
        batches: [],
    };
}

/** Ingresso como `/events/detail/{slug}` manda, no formato do formulário. */
export function ticketFromServer(ticket) {
    if (!ticket.batches || !ticket.batches.length) {
        return { ...newTicket(), ...ticket, internal_id: uuidv4(), batches: [] };
    }

    const [first, ...rest] = ticket.batches;
    const floatPrice = Number.parseFloat(ticket.price);

    const adapted = {
        ...ticket,
        ...purchaseRulesFromTicket(ticket),
        internal_id: uuidv4(),
        sale_type: ticket.sale_type ?? SaleTypes.BATCH,
        // O `sale_start` do produto é o início das vendas (em `opens_at`);
        // aqui o campo é o do período, que vem do primeiro lote.
        sale_start: undefined,
        floatPrice,
        // Preço como estava salvo: mudar o de um lote já vendido merece aviso.
        original_price: floatPrice,
        // Ingresso que já está a zero volta com a chave ligada.
        is_free: Number(ticket.price) === 0,
        is_active: Boolean(first.is_active),
        server_active: Boolean(first.is_active),
        batchName: first.name,
        batch_id: first.id,
        // É o lote que a API compara com o vendido; o do produto só acompanha.
        quantity: first.quantity ?? ticket.quantity,
        hidden_code: ticket.hidden_code ?? null,
        // Piso da quantidade, fixado aqui porque depende do lote
        // como o servidor mandou — `quantity` muda ao digitar.
        committed: committedQuantity(first),
        batches: rest.map(batch => {
            const batchPrice = Number.parseFloat(batch.price);

            return {
                ...batch,
                floatPrice: batchPrice,
                original_price: batchPrice,
                server_active: Boolean(batch.is_active),
                committed: committedQuantity(batch),
                internal_id: uuidv4(),
            };
        }),
    };

    if (adapted.sale_type === SaleTypes.PERIOD) {
        adapted.sale_start = first.sale_start;
        adapted.sale_end = first.sale_end;
    }

    return adapted;
}

/** Todos os lotes do ingresso no mesmo formato, o primeiro incluído. */
export function lotsOf(ticket) {
    const first = {
        key: `${ticket.internal_id}-first`,
        id: ticket.batch_id,
        name: ticket.batchName,
        price: ticket.price,
        floatPrice: ticket.floatPrice,
        quantity: ticket.quantity,
        committed: ticket.committed || 0,
        originalPrice: ticket.original_price,
        serverActive: ticket.server_active,
        is_active: ticket.is_active,
        sale_start: ticket.sale_start,
        sale_end: ticket.sale_end,
        description: ticket.description,
    };

    return [
        first,
        ...ticket.batches.map(batch => ({
            key: batch.internal_id,
            id: batch.id,
            name: batch.name,
            price: batch.price,
            floatPrice: batch.floatPrice,
            quantity: batch.quantity,
            committed: batch.committed || 0,
            originalPrice: batch.original_price,
            serverActive: batch.server_active,
            is_active: batch.is_active,
            sale_start: batch.sale_start,
            sale_end: batch.sale_end,
            description: batch.description,
        })),
    ];
}

// Campo do lote → campo do ingresso, para o Lote 1.
const FIRST_LOT_FIELDS = {
    name: 'batchName',
    price: 'price',
    floatPrice: 'floatPrice',
    quantity: 'quantity',
    sale_start: 'sale_start',
    sale_end: 'sale_end',
};

/** Altera um lote pelo índice da tela (0 é o Lote 1) e devolve o ingresso novo. */
export function patchLot(ticket, index, patch) {
    if (index === 0) {
        const mapped = {};

        Object.entries(patch).forEach(([field, value]) => {
            if (FIRST_LOT_FIELDS[field]) {
                mapped[FIRST_LOT_FIELDS[field]] = value;
            }
        });

        return { ...ticket, ...mapped };
    }

    return {
        ...ticket,
        batches: ticket.batches.map((batch, i) => (i === index - 1 ? { ...batch, ...patch } : batch)),
    };
}

/**
 * Nome do lote como vai para a API. Sem nome digitado, é "{ingresso} Lote N",
 * montado na hora do envio para acompanhar o nome do ingresso enquanto o
 * produtor digita. A API guarda como veio porque o nome começa pelo do
 * produto (`Batch::buildName`).
 */
export function autoLotName(ticket, index) {
    return `${(ticket.name || '').trim() || 'Ingresso'} Lote ${index + 1}`;
}

export function lotName(ticket, lot, index) {
    return (lot.name || '').trim() || autoLotName(ticket, index);
}

/** Meia-entrada não ganha outra meia: o botão some quando o nome já diz que é meia. */
export function isHalfTicket(ticket) {
    return /\bmeia\b/i.test(ticket.name || '');
}

export function newLot(isFree) {
    return {
        internal_id: uuidv4(),
        price: isFree ? '0,00' : '',
        floatPrice: 0,
        quantity: '',
        description: '',
        is_active: false,
    };
}

function priceText(value) {
    return currency.format(value).replace('R$ ', '');
}

/**
 * Cópia sem nada do que é do servidor: ids, vendas e preço original ficam no
 * ingresso de origem. `priceFactor` faz a meia-entrada (0,5) num passo só.
 */
export function cloneTicket(source, { name, priceFactor = 1 } = {}) {
    const scale = value => Math.round((Number(value) || 0) * priceFactor * 100) / 100;
    const firstPrice = scale(source.floatPrice);
    // Nome derivado do ingresso de origem ("Inteira Lote 1", "Inteira Lote (1)")
    // volta a ser automático, para virar "Inteira – Meia Lote 1" na cópia.
    // Nome escolhido pelo produtor ("Pré-venda") segue como está.
    const sourceName = (source.name || '').trim();
    const ownName = lotNameValue => (
        lotNameValue && !(sourceName && lotNameValue.startsWith(sourceName)) && !/^Lote \d+$/.test(lotNameValue)
            ? lotNameValue
            : ''
    );

    return {
        ...JSON.parse(JSON.stringify(source)),
        internal_id: uuidv4(),
        id: undefined,
        batch_id: undefined,
        committed: 0,
        original_price: undefined,
        server_active: undefined,
        name: name ?? source.name,
        batchName: ownName(source.batchName),
        floatPrice: firstPrice,
        price: priceFactor === 1 ? source.price : priceText(firstPrice),
        batches: source.batches.map(batch => {
            const batchPrice = scale(batch.floatPrice);

            return {
                ...batch,
                name: ownName(batch.name),
                internal_id: uuidv4(),
                id: undefined,
                committed: 0,
                original_price: undefined,
                server_active: undefined,
                floatPrice: batchPrice,
                price: priceFactor === 1 ? batch.price : priceText(batchPrice),
            };
        }),
    };
}

/** Nome com sufixo sem estourar o limite de caracteres do ingresso. */
export function nameWithSuffix(name, suffix) {
    const base = (name || '').replace(/ \(cópia\)$/, '').replace(/ – Meia$/, '');

    if (!base) return '';

    return `${base.slice(0, NAME_MAX - suffix.length)}${suffix}`;
}

export function isTicketFree(ticket, isEventFree) {
    return isEventFree || ticket.is_free === true;
}

export function ticketCommitted(ticket) {
    return lotsOf(ticket).reduce((total, lot) => total + lot.committed, 0);
}

export function ticketCapacity(ticket) {
    return lotsOf(ticket).reduce((total, lot) => total + (Number(lot.quantity) || 0), 0);
}

/**
 * Lote em venda agora, pelo que o servidor disse. Lote que esgotou ou ainda
 * não abriu não é o "atual" — os lotes viram sozinhos no servidor.
 */
export function currentLotIndex(ticket) {
    return lotsOf(ticket).findIndex(lot => (
        lot.id && lot.serverActive && lot.committed < (Number(lot.quantity) || 0)
    ));
}

export function isLotSoldOut(lot) {
    const quantity = Number(lot.quantity) || 0;

    return Boolean(lot.id) && quantity > 0 && lot.committed >= quantity;
}

/**
 * O que falta para o ingresso poder ser salvo. Cada item diz o campo e, nos
 * lotes, o índice da tela — é o que a tela usa para pôr o erro sob o campo
 * certo e para contar as pendências na lista.
 */
export function ticketIssues(ticket, { isEventFree }) {
    const issues = [];
    const add = (field, message, lot) => issues.push({ field, message, lot });
    const free = isTicketFree(ticket, isEventFree);
    const isPeriod = ticket.sale_type === SaleTypes.PERIOD;

    if (!ticket.name) add('name', 'Dê um nome ao ingresso');
    else if (ticket.name.length > NAME_MAX) add('name', `Use até ${NAME_MAX} caracteres`);

    if (ticket.description && ticket.description.length > DESCRIPTION_MAX) {
        add('description', `Use até ${DESCRIPTION_MAX} caracteres`);
    }

    lotsOf(ticket).forEach((lot, index) => {
        // Nome em branco é válido (vira o automático); o limite vale só para o
        // que o produtor digitou.
        if (lot.name && lot.name.length > BATCH_NAME_MAX) {
            add('lotName', `Use até ${BATCH_NAME_MAX} caracteres`, index);
        }

        /* Gratuito é válido, mas só quando é escolha declarada. Sem isso, um
           ingresso com o campo de preço em branco (floatPrice 0) passaria pela
           validação e seria salvo como cortesia sem ninguém pedir. */
        if (!free && !(Number(lot.floatPrice) > 0)) {
            add('price', 'Informe o valor ou marque como gratuito', index);
        }

        const quantity = Number(lot.quantity) || 0;

        if (!quantity) {
            add('quantity', 'Informe a quantidade', index);
        } else {
            // Reduzir abaixo do que já saiu é recusado pelo servidor; travar
            // aqui evita o produtor descobrir só depois de salvar.
            const floorError = quantityFloorError(lot.committed, quantity);

            if (floorError) add('quantity', floorError, index);
        }

        if (isPeriod && !(lot.sale_start && lot.sale_end)) {
            add('period', 'Preencha início e fim das vendas deste lote', index);
        }
    });

    if (ticket.hidden_code && ticket.hidden_code.length > HIDDEN_CODE_MAX) {
        add('hidden_code', `Use até ${HIDDEN_CODE_MAX} caracteres`);
    }

    if (hasSaleStart(ticket) && !ticket.opens_at) {
        add('opens_at', 'Informe a data e o horário de abertura');
    }

    if (ticket.has_max_per_order && !isMaxPerOrderValid(ticket.max_per_order)) {
        add('max_per_order', `Informe um número de 1 a ${MAX_PER_ORDER_LIMIT}`);
    }

    if (!isBuyerMessageValid(ticket)) {
        add('buyer_message', 'Revise o recado para quem comprar');
    }

    return issues;
}

/** Campos que ficam em "Mais opções" — a seção abre sozinha quando um deles tem erro. */
export const OPTION_FIELDS = ['hidden_code', 'opens_at', 'max_per_order', 'buyer_message'];

/** Uma linha por lote: preço cobrado, taxa e o que o produtor recebe. */
export function lotIncome(lot, { free, absorbed, calculateFee, calculateFeePix }) {
    const price = free ? 0 : Number(lot.floatPrice) || 0;

    if (!absorbed) {
        return { price, net: price, netPix: price };
    }

    const net = price - calculateFee(price);
    const netPix = price - calculateFeePix(price);

    return { price, net: net > 0 ? net : 0, netPix: netPix > 0 ? netPix : 0 };
}

/** Totais do evento para a barra de baixo. */
export function eventTotals(tickets, { isEventFree, absorbed, calculateFee, calculateFeePix }) {
    return tickets.reduce((totals, ticket) => {
        const free = isTicketFree(ticket, isEventFree);

        lotsOf(ticket).forEach(lot => {
            const quantity = Number(lot.quantity) || 0;
            const { price, net } = lotIncome(lot, { free, absorbed, calculateFee, calculateFeePix });

            totals.capacity += quantity;
            totals.committed += lot.committed;
            totals.gross += price * quantity;
            totals.net += net * quantity;
        });

        return totals;
    }, { capacity: 0, committed: 0, gross: 0, net: 0 });
}

/** Payload de um ingresso para `createBulk`/`updateBulk`; `withIds` só na edição. */
export function ticketPayload(ticket, { eventId, isEventFree, withIds }) {
    const firstBatch = {
        price: ticket.floatPrice,
        name: lotName(ticket, { name: ticket.batchName }, 0),
        quantity: ticket.quantity,
        is_active: ticket.is_active,
    };

    if (withIds) firstBatch.id = ticket.batch_id;

    if (ticket.sale_start && ticket.sale_end) {
        firstBatch.sale_start = ticket.sale_start;
        firstBatch.sale_end = ticket.sale_end;
    }

    const payload = {
        event_id: eventId,
        is_active: ticket.is_active,
        type: 'ticket',
        name: ticket.name,
        price: ticket.floatPrice,
        quantity: Number(ticket.quantity),
        sale_type: ticket.sale_type,
        hidden_code: ticket.hidden_code || null,
        absorbed_service_fee: ticket.absorbed_service_fee ?? false,
        requires_document: ticket.requires_document ?? false,
        ...buyerMessagePayload(ticket),
        ...purchaseRulesPayload(ticket),
        batches: [firstBatch, ...ticket.batches.map((batch, i) => {
            const adapted = {
                price: batch.floatPrice,
                name: lotName(ticket, batch, i + 1),
                quantity: batch.quantity,
                is_active: batch.is_active,
            };

            if (withIds) adapted.id = batch.id;

            if (batch.description) adapted.description = batch.description;

            if (batch?.sale_start && batch?.sale_end) {
                adapted.sale_start = batch.sale_start;
                adapted.sale_end = batch.sale_end;
            }

            return adapted;
        })],
    };

    if (withIds) payload.id = ticket.id;

    if (ticket.description) {
        firstBatch.description = ticket.description;
        payload.description = ticket.description;
    }

    /* Preço zero passou a ser estado do ingresso, não só do evento:
       o back precisa do `is_free` para tratar a compra como cortesia,
       e os lotes acompanham — lote pago dentro de ingresso grátis
       cobraria o comprador que a tela prometeu não cobrar. */
    const free = isTicketFree(ticket, isEventFree);

    payload.is_free = free;

    if (free) {
        payload.price = 0;
        payload.batches = payload.batches.map(batch => ({ ...batch, price: 0 }));
    }

    return payload;
}
